import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface Position {
  id: string
  symbol: string
  assetType: 'us_stock' | 'hk_stock' | 'crypto'
  quantity: number
  averageCost: number
  currentPrice: number
  marketValue: number
  unrealizedPnL: number
  unrealizedPnLPercent: number
}

export interface Transaction {
  id: string
  symbol: string
  side: 'buy' | 'sell'
  quantity: number
  price: number
  fee: number
  total: number
  createdAt: string
}

interface PortfolioState {
  totalValue: number
  todayPnL: number
  todayPnLPercent: number
  availableBalance: number
  positions: Position[]
  transactions: Transaction[]
  loading: boolean
  error: string | null
}

const initialState: PortfolioState = {
  totalValue: 0,
  todayPnL: 0,
  todayPnLPercent: 0,
  availableBalance: 0,
  positions: [],
  transactions: [],
  loading: false,
  error: null,
}

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    fetchPortfolioStart: (state) => {
      state.loading = true
      state.error = null
    },
    fetchPortfolioSuccess: (state, action: PayloadAction<{
      totalValue: number
      todayPnL: number
      todayPnLPercent: number
      availableBalance: number
      positions: Position[]
    }>) => {
      state.loading = false
      state.totalValue = action.payload.totalValue
      state.todayPnL = action.payload.todayPnL
      state.todayPnLPercent = action.payload.todayPnLPercent
      state.availableBalance = action.payload.availableBalance
      state.positions = action.payload.positions
      state.error = null
    },
    fetchPortfolioFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
    },
    updatePositionPrice: (state, action: PayloadAction<{ symbol: string; currentPrice: number }>) => {
      const position = state.positions.find(p => p.symbol === action.payload.symbol)
      if (position) {
        position.currentPrice = action.payload.currentPrice
        position.marketValue = position.quantity * action.payload.currentPrice
        position.unrealizedPnL = position.marketValue - (position.quantity * position.averageCost)
        position.unrealizedPnLPercent = (position.unrealizedPnL / (position.quantity * position.averageCost)) * 100
      }
      
      // Recalculate total value
      state.totalValue = state.availableBalance + state.positions.reduce((sum, pos) => sum + pos.marketValue, 0)
    },
    addTransaction: (state, action: PayloadAction<Transaction>) => {
      state.transactions.unshift(action.payload)
    },
    clearError: (state) => {
      state.error = null
    },
  },
})

export const {
  fetchPortfolioStart,
  fetchPortfolioSuccess,
  fetchPortfolioFailure,
  updatePositionPrice,
  addTransaction,
  clearError,
} = portfolioSlice.actions

export default portfolioSlice.reducer